import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createIndividualSignup } from "../../api/api";
import { adoptAccount } from "../../lib/sessionActions";
import { Checkbox } from "../ui/checkbox";

/**
 * Review and submit individual signup.
 *
 * @param {Object} props
 * @param {Object} props.formData
 * @param {Object} props.beliefs
 * @param {boolean} props.termsAgreed
 * @param {function(): void} props.onBack
 * @param {function(Object): void} [props.onSetUser]
 * @returns {JSX.Element}
 */
export default function UserSubmit({
  formData,
  beliefs,
  termsAgreed,
  onBack,
  onSetUser,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [informationVerified, setInformationVerified] = useState(false);
  const navigate = useNavigate();
  const fullName = `${formData.first_name || ""} ${formData.last_name || ""}`.trim();

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError("");
    const payload = {
      username: formData.username,
      password: formData.password,
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      phone: formData.phone || "",
      location: formData.location || "",
      latitude: formData.latitude ?? 0,
      longitude: formData.longitude ?? 0,
      pfp_url: formData.pfp_url || "",
      denomination: beliefs?.denomination || "",
    };
    try {
      const result = await createIndividualSignup(payload);
      if (result.error) {
        setError(result.error);
        setIsSubmitting(false);
        return;
      }
      const account = result.data ? { ...result.data, type: result.data.type || "user" } : null;
      if (result.token && account) {
        const adopted = await adoptAccount(account, result.token);
        if (adopted.error) {
          setError(adopted.error);
          setIsSubmitting(false);
          return;
        }
      }
      if (onSetUser && account) {
        onSetUser(account);
      }
      const userId = account?.id;
      navigate(userId ? `/profile/${userId}` : "/");
    } catch (err) {
      setError("Network error or server unavailable");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="mb-4 text-2xl font-bold text-text-primary">Review & Submit</h2>
      <div className="rounded-lg border border-border bg-tertiary p-6">
        <h3 className="mb-3 text-lg font-semibold text-text-primary">Review process</h3>
        <p className="text-text-secondary">
          Your account will be reviewed by our team to confirm authenticity. You will be notified by
          email once your account has been accepted or if any additional information is needed.
        </p>
      </div>

      <div className="space-y-4 rounded-lg border border-border bg-tertiary p-6">
        <h3 className="text-lg font-semibold text-text-primary">Your information</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-medium text-text-secondary">Name</label>
            <p className="mt-1 text-text-primary">{fullName || "Not provided"}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-text-secondary">Username</label>
            <p className="mt-1 text-text-primary">{formData.username || "Not provided"}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-text-secondary">Email</label>
            <p className="mt-1 text-text-primary">{formData.email || "Not provided"}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-text-secondary">City / state</label>
            <p className="mt-1 text-text-primary">{formData.location || "Not provided"}</p>
          </div>
          {formData.phone ? (
            <div>
              <label className="text-sm font-medium text-text-secondary">Phone</label>
              <p className="mt-1 text-text-primary">{formData.phone}</p>
            </div>
          ) : null}
          <div>
            <label className="text-sm font-medium text-text-secondary">Denomination</label>
            <p className="mt-1 text-text-primary">{beliefs?.denomination || "Not provided"}</p>
          </div>
        </div>
        {termsAgreed ? (
          <div className="flex items-center space-x-2 border-t border-border pt-4">
            <Checkbox checked disabled />
            <label className="text-sm text-text-secondary">
              I have read and agree to the terms and conditions
            </label>
          </div>
        ) : null}
        <div className="flex items-start space-x-3 border-t border-border pt-4">
          <Checkbox
            id="user-information-verification"
            checked={informationVerified}
            onCheckedChange={setInformationVerified}
            className="mt-1 h-5 w-5"
          />
          <label htmlFor="user-information-verification" className="cursor-pointer text-sm text-text-secondary">
            I verify that all information provided is accurate and truthful. I understand that providing
            false or misleading information may result in the rejection of this application or removal
            from the platform.
          </label>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-error bg-error/20 p-4">
          <p className="text-error">{error}</p>
        </div>
      ) : null}

      <div className="flex justify-between border-t border-border pt-6">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="rounded-md border border-border bg-secondary px-6 py-2 text-text-primary transition hover:bg-tertiary disabled:opacity-50"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting || !informationVerified}
          className={`rounded-md px-6 py-2 transition ${
            isSubmitting || !informationVerified
              ? "cursor-not-allowed bg-secondary text-text-secondary opacity-50"
              : "bg-accent text-text-on-accent hover:bg-accent-hover"
          }`}
        >
          {isSubmitting ? "Submitting..." : "Submit application"}
        </button>
      </div>
    </div>
  );
}
